'use client';
import type React from 'react';
import { useMemo } from 'react';

import { advertisementRenderLink } from '@/components/advertisement/advertisementLink';

import { AdPlacement } from '@cineamo/api/models/advertisement/AdPlacement.enum';

import AdvertisementRenderer from '@cineamo/composables/src/advertisement/AdvertisementRenderer';

import { usePathname } from '@/i18n/routing';
import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';
import { useUserAgent } from '@/providers/UserAgentProvider';

type AdvertisementProps = {
    type: AdPlacement;
};

export const Advertisement: React.FC<AdvertisementProps> = ({ type }) => {
    const pathname = usePathname();
    const domainConfiguration = useDomainConfiguration();
    const isMobileUserAgent = useUserAgent().isMobile;
    const currentDate = useMemo(() => new Date(), []);

    // Top banner only ships on the homepage for now — a cinema-website page rule, not a
    // composable concern (the shared AdvertisementRenderer/BannerAdvertisement no longer know
    // about pages).
    if (type === AdPlacement.TOP_BANNER && pathname !== '/') {
        return null;
    }

    const cinemaInfoBanner = domainConfiguration?.websiteConfiguration?.infoBanner;
    const isInfoBannerVisible =
        cinemaInfoBanner?.text?.length > 0 &&
        (cinemaInfoBanner
            ? (cinemaInfoBanner.activeStartDatetime
                  ? currentDate >= new Date(cinemaInfoBanner.activeStartDatetime)
                  : true) &&
              (cinemaInfoBanner.activeEndDatetime ? currentDate < new Date(cinemaInfoBanner.activeEndDatetime) : true)
            : false);

    return (
        <AdvertisementRenderer
            type={type}
            renderLink={advertisementRenderLink}
            isInfoBannerVisible={isInfoBannerVisible}
            isMobileUserAgent={isMobileUserAgent}
            backdropColor={domainConfiguration.style.backgroundColor}
        />
    );
};
